import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Send, Eye, Calendar, History, Loader2, Check, AlertCircle, MinusCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { ScrollPanel } from "../ui/scroll-panel";
import { Switch } from "../ui/switch";

/**
 * QENVA · Daily Brief Settings.
 *
 * Per-user opt-in for the daily email summary (Guardian Score, alerts,
 * opportunities, CrossCheck pulse). Provides:
 * - Enable toggle
 * - Frequency (daily / weekly)
 * - Locale (FR / EN)
 * - Preview button (renders inline)
 * - Send-test (delivers via Resend immediately)
 * - History (last 10 sends, sent/failed/skipped)
 */
export default function DailyBriefSection() {
  const { t } = useTranslation("common");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await api.get("/daily-brief/config");
      setConfig(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await api.get("/daily-brief/history", { params: { limit: 10 } });
      setHistory(r.data?.items || []);
    } catch (e) {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchHistory();
  }, [fetchConfig, fetchHistory]);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/daily-brief/config", patch);
      setConfig(r.data);
      toast.success(t("settings.daily_brief.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const r = await api.post("/daily-brief/preview", null, {
        params: { locale: config?.locale || "fr" },
      });
      setPreviewHtml(r.data.html);
      setPreviewSubject(r.data.subject);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur prévisualisation");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post("/daily-brief/send-test");
      const status = r.data?.status;
      if (status === "sent") {
        toast.success(t("settings.daily_brief.sent_ok"));
      } else if (status === "skipped") {
        toast.message(t("settings.daily_brief.sent_skipped"));
      } else {
        toast.error(`${t("settings.daily_brief.sent_failed")}: ${r.data?.error || ""}`);
      }
      fetchHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur envoi");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-6 mt-6"
      data-testid="daily-brief-section"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600 inline-flex items-center gap-1.5">
            <Mail size={12} strokeWidth={1.75} />
            QENVA · Daily Brief
          </p>
          <h2 className="font-display text-2xl font-semibold text-qenva-navy mt-1">
            {t("settings.daily_brief.title")}
          </h2>
          <p className="text-sm text-zinc-500 mt-1.5 max-w-xl">
            {t("settings.daily_brief.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-zinc-500 font-medium">
            {config.enabled
              ? t("settings.daily_brief.enabled")
              : t("settings.daily_brief.disabled")}
          </span>
          <Switch
            checked={!!config.enabled}
            disabled={saving}
            onCheckedChange={(v) => saveConfig({ enabled: !!v })}
            data-testid="daily-brief-enabled-toggle"
          />
        </div>
      </div>

      {/* Controls */}
      <div className={`mt-6 grid gap-4 sm:grid-cols-2 ${config.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1.5">
            <Calendar size={11} className="inline mr-1" />
            {t("settings.daily_brief.frequency")}
          </p>
          <div className="inline-flex p-1 bg-zinc-100 rounded-full border border-zinc-200">
            {["daily", "weekly"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => saveConfig({ frequency: f })}
                disabled={saving}
                data-testid={`daily-brief-freq-${f}`}
                className={`px-3 py-1 text-[12px] font-semibold rounded-full transition-colors ${
                  config.frequency === f
                    ? "bg-white text-qenva-navy shadow-sm"
                    : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                {t(`settings.daily_brief.freq_${f}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1.5">
            {t("settings.daily_brief.language")}
          </p>
          <div className="inline-flex p-1 bg-zinc-100 rounded-full border border-zinc-200">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => saveConfig({ locale: l })}
                disabled={saving}
                data-testid={`daily-brief-lang-${l}`}
                className={`px-3 py-1 text-[12px] font-semibold rounded-full uppercase transition-colors ${
                  config.locale === l
                    ? "bg-white text-qenva-navy shadow-sm"
                    : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={openPreview}
          disabled={previewLoading}
          data-testid="daily-brief-preview-btn"
        >
          <Eye size={14} className="mr-1.5" />
          {t("settings.daily_brief.preview")}
        </Button>
        <Button
          size="sm"
          onClick={sendTest}
          disabled={sending}
          data-testid="daily-brief-send-test-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
          {t("settings.daily_brief.send_test")}
        </Button>
      </div>

      {/* History */}
      <div className="mt-8 border-t border-zinc-100 pt-5">
        <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-zinc-500 inline-flex items-center gap-1.5">
          <History size={11} />
          {t("settings.daily_brief.history")}
        </p>
        {history.length === 0 ? (
          <p className="text-sm text-zinc-400 mt-3" data-testid="daily-brief-history-empty">
            {t("settings.daily_brief.no_history")}
          </p>
        ) : (
          <ul className="mt-3 space-y-1.5" data-testid="daily-brief-history-list">
            {history.map((h) => (
              <li
                key={h.id}
                className="flex items-center gap-3 text-[12.5px] py-1.5 border-b border-zinc-100 last:border-0"
                data-testid={`daily-brief-history-${h.id}`}
              >
                <StatusIcon status={h.status} />
                <span className="text-zinc-700 truncate flex-1 min-w-0">{h.subject}</span>
                <span className="text-zinc-400 tabular-nums shrink-0">
                  {new Date(h.sent_at).toLocaleString(config.locale === "en" ? "en-GB" : "fr-FR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <PreviewModal
          html={previewHtml}
          subject={previewSubject}
          loading={previewLoading}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </section>
  );
}

function StatusIcon({ status }) {
  if (status === "sent")
    return <Check size={14} className="text-emerald-600 shrink-0" />;
  if (status === "failed")
    return <AlertCircle size={14} className="text-rose-500 shrink-0" />;
  return <MinusCircle size={14} className="text-zinc-400 shrink-0" />;
}

function PreviewModal({ html, subject, loading, onClose }) {
  const { t } = useTranslation("common");
  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="daily-brief-preview-modal"
    >
      <div
        className="bg-zinc-100 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 bg-white flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500">{t("settings.daily_brief.preview")}</p>
            <p className="text-[13px] font-semibold text-qenva-navy truncate">{subject}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="daily-brief-preview-close">
            ✕
          </Button>
        </div>
        <ScrollPanel>
          {loading ? (
            <div className="p-10 grid place-items-center text-zinc-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <iframe
              srcDoc={html}
              className="w-full h-[70vh] bg-zinc-100"
              title="Daily Brief preview"
              sandbox="allow-same-origin"
              data-testid="daily-brief-preview-iframe"
            />
          )}
        </ScrollPanel>
      </div>
    </div>
  );
}
